/**
 * Modello e utility condivise per la gestione multi-sede delle sedi operative
 * nelle form "Dati Specifici" dei fiduciari in scope (perito auto, perito RE,
 * investigatore, medico, legale).
 *
 * Regole (concordate con l'ufficio AXA Gestione Fiduciari):
 * - si indica se la sede legale è anche una sede operativa (flag `sameAsLegal`);
 * - se NON lo è, almeno una sede operativa è obbligatoria;
 * - in ogni caso è possibile aggiungere N sedi operative aggiuntive facoltative.
 *
 * Il backend (`operational_site` + `replaceSites`) è già multi-sede: ogni sede è
 * un elemento dell'array `operationalSites[]` con un flag `legalFallback`
 * (address nullo quando coincide con la sede legale).
 */

/** View-model di una singola sede operativa con indirizzo esplicito. */
export interface OperatingSiteVM {
  /** Chiave stabile solo lato UI (per il track dell'@for). */
  key: number;
  /** Id della sede persistita (round-trip per non perdere i Codici SOL associati); null se nuova. */
  id: number | null;
  streetType: string;
  streetName: string;
  streetNumber: string;
  zipCode: string;
  region: string;
  province: string;
  municipality: string;
}

/** Esito del parsing della risposta backend in stato UI. */
export interface LoadedOperatingSites {
  /** La sede legale è anche una sede operativa. */
  sameAsLegal: boolean;
  /** Id della sede "legalFallback" persistita (round-trip). */
  legalSiteId: number | null;
  /** Sedi operative con indirizzo esplicito. */
  sites: OperatingSiteVM[];
}

let siteKeyCounter = 1;

/** Genera una nuova sede operativa vuota con chiave UI univoca. */
export function emptyOperatingSite(): OperatingSiteVM {
  return {
    key: siteKeyCounter++,
    id: null,
    streetType: '',
    streetName: '',
    streetNumber: '',
    zipCode: '',
    region: '',
    province: '',
    municipality: '',
  };
}

/**
 * Trasforma l'array `operationalSites[]` restituito dal backend nello stato UI.
 * Retro-compatibile con il caso mono-sede precedente:
 * - elemento con `legalFallback === true` (o senza address) => la sede legale è operativa;
 * - elementi con address => sedi operative esplicite;
 * - array vuoto/assente => default "coincide con la sede legale".
 */
export function sitesFromResponse(operationalSites: any[] | undefined | null): LoadedOperatingSites {
  const list = Array.isArray(operationalSites) ? operationalSites : [];
  let sameAsLegal = false;
  let legalSiteId: number | null = null;
  const sites: OperatingSiteVM[] = [];

  for (const s of list) {
    const addr = s?.address;
    if (s?.legalFallback === true || !addr) {
      sameAsLegal = true;
      legalSiteId = s?.id ?? legalSiteId;
      continue;
    }
    sites.push({
      key: siteKeyCounter++,
      id: s?.id ?? null,
      streetType: addr.streetType || '',
      streetName: addr.streetName || '',
      streetNumber: addr.streetNumber != null ? String(addr.streetNumber) : '',
      zipCode: addr.zipCode || '',
      region: addr.region || '',
      province: addr.province || '',
      municipality: addr.municipality || '',
    });
  }

  if (list.length === 0) {
    // Nessuna sede persistita: default "coincide con la sede legale".
    sameAsLegal = true;
  }

  return { sameAsLegal, legalSiteId, sites };
}

/**
 * Costruisce il payload `operationalSites[]` per il backend a partire dallo stato UI.
 * Quando la sede legale è operativa, aggiunge in testa l'elemento `legalFallback`
 * (address nullo) preservandone l'id persistito.
 */
export function sitesToRequest(
  sameAsLegal: boolean,
  legalSiteId: number | null,
  sites: OperatingSiteVM[],
): any[] {
  const out: any[] = [];

  if (sameAsLegal) {
    out.push({ id: legalSiteId, legalFallback: true, address: null });
  }

  for (const s of sites) {
    out.push({
      id: s.id,
      legalFallback: false,
      address: {
        streetType: s.streetType,
        streetName: s.streetName,
        streetNumber: s.streetNumber || '',
        zipCode: s.zipCode,
        municipality: s.municipality,
        province: s.province,
        region: s.region,
      },
    });
  }

  return out;
}

/**
 * Valida le sedi operative e ritorna la lista di messaggi d'errore per il riepilogo.
 * Tutti i messaggi iniziano con "Sede operativa" così da poter essere filtrati.
 */
export function validateOperationalSites(sameAsLegal: boolean, sites: OperatingSiteVM[]): string[] {
  const errors: string[] = [];

  if (!sameAsLegal && sites.length === 0) {
    errors.push('Sede operativa: indicare almeno una sede operativa');
  }

  sites.forEach((s, i) => {
    const label = `Sede operativa ${i + 1}`;
    if (!s.streetType || s.streetType.trim() === '') errors.push(`${label}: Tipo Via`);
    if (!s.streetName || s.streetName.trim() === '') errors.push(`${label}: Nome Via`);
    if (!s.streetNumber || String(s.streetNumber).trim() === '') errors.push(`${label}: Numero`);
    if (!s.zipCode || s.zipCode.trim() === '') errors.push(`${label}: CAP`);
    else if (!/^\d{5}$/.test(s.zipCode)) errors.push(`${label}: CAP (deve essere di 5 cifre)`);
    if (!s.region || s.region.trim() === '') errors.push(`${label}: Regione`);
    if (!s.province || s.province.trim() === '') errors.push(`${label}: Provincia`);
    if (!s.municipality || s.municipality.trim() === '') errors.push(`${label}: Comune`);
  });

  return errors;
}
